import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import PageShell from '../components/chrome/PageShell';
import BlockedUsersSection from '../components/BlockedUsersSection';
import ChangeEmailSection from '../components/ChangeEmailSection';
import ChangePhoneSection from '../components/ChangePhoneSection';
import { stripeService, profileService } from '../services';
import { useUser, useAuth } from '../context';
import useMediaQuery from '../utils/useMediaQuery';
import useTopBarHeight from '../hooks/useTopBarHeight';
import { A, Card, CardTitle, Dot, FieldLabel, Input, PageTitle, Pill, Prose, RADIUS, SectionHead, StatusPill, money } from '../components/account/kit';

const WAITING_QUIPS = [
  "Stripe is squinting at your selfie...",
  "Convincing robots you're a real human...",
  "Even the DMV is faster than this...",
  "Your identity is buffering...",
  "Stripe said they'd call back...",
  "A Stripe intern is Googling you rn...",
  "Verifying that they're verifying...",
  "Have you tried being more verifiable?",
  "Your verification is in another castle...",
  "At this point Stripe knows you better than your therapist...",
  "Somewhere, a hamster is running a compliance wheel...",
  "They're probably just on lunch...",
  "Stripe is checking your vibe...",
  "Asking your mom for a second opinion...",
  "Running your name through the cool kids list...",
];

const REQUIREMENT_LABELS = {
  'individual.verification.document': 'Government-issued ID',
  'individual.verification.additional_document': 'Additional identity document',
  'individual.verification.proof_of_liveness': 'Selfie or liveness check',
  'individual.id_number': 'SSN or government ID number',
  'individual.dob.day': 'Date of birth',
  'individual.dob.month': 'Date of birth',
  'individual.dob.year': 'Date of birth',
  'individual.address.line1': 'Home address',
  'individual.address.city': 'Home address (city)',
  'individual.address.state': 'Home address (state)',
  'individual.address.postal_code': 'Home address (postal code)',
  'individual.ssn_last_4': 'Last 4 digits of SSN',
  'individual.phone': 'Phone number',
  'individual.email': 'Email address',
  'individual.first_name': 'Legal first name',
  'individual.last_name': 'Legal last name',
  'individual.relationship.title': 'Your role/title',
  'external_account': 'Bank account for payouts',
  'tos_acceptance.date': 'Terms of service acceptance',
  'tos_acceptance.ip': 'Terms of service acceptance',
};

function formatRequirement(code) {
  if (REQUIREMENT_LABELS[code]) return REQUIREMENT_LABELS[code];
  const prefix = Object.keys(REQUIREMENT_LABELS).find((key) => code.startsWith(key));
  if (prefix) return REQUIREMENT_LABELS[prefix];
  const parts = code.split('.');
  return parts[parts.length - 1].replace(/[_]/g, ' ');
}

const RECHECK_INTERVAL = 15; // seconds between verification rechecks
const SECTIONS = [
  { key: 'account', label: 'ACCOUNT' },
  { key: 'money', label: 'MONEY' },
  { key: 'privacy', label: 'PRIVACY' },
  { key: 'danger', label: 'DELETE ACCOUNT', danger: true },
];

/**
 * /settings (Sept 2026 redesign, handoff §1): ACCOUNT · MONEY · PRIVACY · DELETE ACCOUNT as
 * sections down one column, with a sticky section nav + wallet shortcut on the left at ≥ 980.
 * `#money` (and the other section keys) on the URL scrolls to that section.
 */
export default function SettingsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hash } = useLocation();
  const { profile, walletBalance, fetchProfile, deleteAccount } = useUser();
  const { user, logout } = useAuth();
  const phone = useMediaQuery('(max-width: 639px)');
  const wide = useMediaQuery('(min-width: 980px)');
  const headerH = useTopBarHeight();

  const [verification, setVerification] = useState(null);
  const [isLoadingVerification, setIsLoadingVerification] = useState(true);
  const [isOnboarding, setIsOnboarding] = useState(false);
  const [verificationError, setVerificationError] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [isRechecking, setIsRechecking] = useState(false);
  const [quipIndex, setQuipIndex] = useState(() => Math.floor(Math.random() * WAITING_QUIPS.length));
  const countdownRef = useRef(null);
  const verificationStatus = verification?.verificationStatus || 'not_started';

  const [currency, setCurrency] = useState(profile?.currencyCode || 'cad');
  const [currencyBusy, setCurrencyBusy] = useState(false);
  useEffect(() => { if (profile?.currencyCode) setCurrency(profile.currencyCode); }, [profile?.currencyCode]);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const [active, setActive] = useState('account');

  const fetchVerification = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setIsLoadingVerification(true);
    if (silent) setIsRechecking(true);
    const result = await stripeService.getVerificationStatus();
    if (result.success) setVerification(result.data);
    setIsLoadingVerification(false);
    setIsRechecking(false);
  }, []);

  useEffect(() => { fetchVerification(); }, [fetchVerification]);

  // Back from Stripe — poll a few times to catch a delayed status update.
  useEffect(() => {
    if (searchParams.get('stripe') !== 'return') return;
    let attempts = 0;
    const poll = async () => {
      await fetchVerification({ silent: true });
      attempts += 1;
      if (attempts < 3) setTimeout(poll, 2000);
    };
    poll();
  }, [searchParams, fetchVerification]);

  useEffect(() => {
    const shouldPoll = verificationStatus === 'pending' || verificationStatus === 'requires_action';
    if (!shouldPoll || isLoadingVerification) {
      if (countdownRef.current) clearInterval(countdownRef.current);
      return undefined;
    }
    setCountdown(RECHECK_INTERVAL);
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          fetchVerification({ silent: true });
          setQuipIndex((i) => (i + 1) % WAITING_QUIPS.length);
          return RECHECK_INTERVAL;
        }
        return prev - 1;
      });
    }, 1000);
    return () => { if (countdownRef.current) clearInterval(countdownRef.current); };
  }, [verificationStatus, isLoadingVerification, fetchVerification]);

  const balance = walletBalance || 0;

  // Scroll to a section: #money from the wallet, or a nav click.
  const go = useCallback((key, smooth = true) => {
    setActive(key);
    const el = document.getElementById(`set-${key}`);
    if (!el) return;
    window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - (headerH + 24), behavior: smooth ? 'smooth' : 'auto' });
  }, [headerH]);
  useEffect(() => {
    const key = hash.replace('#', '');
    if (SECTIONS.some((s) => s.key === key)) setTimeout(() => go(key, false), 50);
  }, [hash, go]);

  const handleStartVerification = async () => {
    setIsOnboarding(true);
    setVerificationError(null);
    const result = await stripeService.startOnboarding();
    if (result.success && result.data?.onboardingUrl) window.location.href = result.data.onboardingUrl;
    else { setVerificationError(result.error || 'Failed to start verification. Please try again.'); setIsOnboarding(false); }
  };

  const handleViewDashboard = async () => {
    const result = await stripeService.getDashboardLink();
    if (result.success && result.data?.url) window.open(result.data.url, '_blank');
  };

  const pickCurrency = async (code) => {
    if (code === currency || currencyBusy) return;
    setCurrencyBusy(true);
    const prev = currency;
    setCurrency(code);
    try {
      await profileService.updateProfile({ currencyCode: code });
      await fetchProfile?.();
    } catch {
      setCurrency(prev);
    }
    setCurrencyBusy(false);
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    setDeleteError(null);
    const result = await deleteAccount();
    if (result.success) { await logout(); navigate('/'); } else { setDeleteError(result.error || 'Failed to delete account'); setIsDeleting(false); }
  };

  const phoneUnverified = !!user?.phoneNumber && !user?.phoneVerified;
  const needsYou = phoneUnverified;
  const cardPad = phone ? 18 : 24;

  const statusPill = () => {
    switch (verificationStatus) {
      case 'verified': return <StatusPill bg={A.verified}>VERIFIED</StatusPill>;
      case 'pending': return <StatusPill bg={A.held} fg={A.ink}>PENDING</StatusPill>;
      case 'requires_action': return <StatusPill bg={A.live}>REQUIRES ACTION</StatusPill>;
      case 'rejected': return <StatusPill bg={A.danger}>REJECTED</StatusPill>;
      default: return <StatusPill bg={A.off} fg={A.ink}>NOT STARTED</StatusPill>;
    }
  };

  const recheckLine = (color) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: 11, fontStyle: 'italic', color: A.grey400 }}>{WAITING_QUIPS[quipIndex]}</span>
      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.14em', color: A.grey500 }}>{isRechecking ? 'RECHECKING…' : `RECHECKING IN ${countdown}S`}</span>
      <div style={{ height: 6, borderRadius: 999, background: A.off, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${((RECHECK_INTERVAL - countdown) / RECHECK_INTERVAL) * 100}%`, background: color, transition: 'width 1s linear' }} />
      </div>
    </div>
  );

  return (
    <PageShell bare>
      <main style={{ maxWidth: 1120, margin: '0 auto', padding: phone ? '20px 16px 64px' : '32px 28px 80px', boxSizing: 'border-box', color: A.ink }}>
        <PageTitle
          phone={phone}
          title="SETTINGS"
          meta={(
            <>
              {needsYou && (<><Dot /><span style={{ color: A.ink, fontWeight: 700 }}>1 THING NEEDS YOU</span><span>·</span></>)}
              <span>{user?.email}</span>
            </>
          )}
        />

        <div style={{ display: 'grid', gridTemplateColumns: wide ? '240px minmax(0,1fr)' : 'minmax(0,1fr)', gap: 24, alignItems: 'start' }}>
          {wide && (
            <aside style={{ position: 'sticky', top: headerH + 24, display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
              <nav aria-label="Settings sections" style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: 8, borderRadius: RADIUS.panel, background: A.white }}>
                {SECTIONS.map((s) => {
                  const on = active === s.key;
                  return (
                    <button
                      key={s.key}
                      type="button"
                      onClick={() => go(s.key)}
                      aria-current={on ? 'true' : undefined}
                      className="pr-acct-nav-row"
                      style={{ display: 'flex', alignItems: 'center', gap: 12, height: 48, padding: '0 14px 0 16px', border: 0, borderRadius: RADIUS.row, background: on ? A.ink : A.white, color: on ? '#fff' : (s.danger ? A.danger : A.ink), fontFamily: 'inherit', textAlign: 'left', cursor: 'pointer', transition: 'background-color .2s, color .2s' }}
                    >
                      <span style={{ flex: 1, minWidth: 0, fontSize: 11, fontWeight: 700, letterSpacing: '.12em', whiteSpace: 'nowrap' }}>{s.label}</span>
                      {s.key === 'account' && phoneUnverified && <Dot />}
                    </button>
                  );
                })}
              </nav>
              <button type="button" onClick={() => navigate('/wallet')} className="pr-acct-invert" style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: 16, border: 0, borderRadius: RADIUS.panel, background: A.white, color: A.ink, fontFamily: 'inherit', textAlign: 'left', cursor: 'pointer', transition: 'background-color .2s, color .2s' }}>
                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.14em', opacity: 0.6 }}>WALLET</span>
                <span style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontSize: 20, fontWeight: 700 }}>{money(balance)}</span>
                  <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.14em' }}>HISTORY →</span>
                </span>
              </button>
            </aside>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 32, minWidth: 0 }}>
            {/* ACCOUNT */}
            <section id="set-account" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <SectionHead label="ACCOUNT" />
              <ChangeEmailSection pad={cardPad} />
              <ChangePhoneSection pad={cardPad} />

              <Card pad={cardPad} data-testid="identity-card">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '.12em' }}>IDENTITY VERIFICATION</span>
                      {!isLoadingVerification && statusPill()}
                    </span>
                    {isLoadingVerification ? (
                      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.14em', color: A.grey400 }}>LOADING…</span>
                    ) : verificationStatus === 'verified' ? (
                      <Prose>Identity verified</Prose>
                    ) : verificationStatus === 'pending' ? (
                      <Prose>Your verification is being processed. This usually takes a few minutes.</Prose>
                    ) : verificationStatus === 'requires_action' ? (
                      <Prose>Stripe’s being a party pooper and needs more info from you. At least you know your financial info is secure 🙌</Prose>
                    ) : verificationStatus === 'rejected' ? (
                      <Prose color={A.danger}>Verification was unsuccessful. Please try again or contact support.</Prose>
                    ) : (
                      <Prose>Verify your identity to enable $ cash-out. Stripe handles all verification securely.</Prose>
                    )}
                  </div>
                  {!isLoadingVerification && verificationStatus === 'verified' && (
                    <Pill kind="secondary" onClick={handleViewDashboard}>VIEW PAYOUT DASHBOARD</Pill>
                  )}
                  {!isLoadingVerification && verificationStatus !== 'verified' && verificationStatus !== 'pending' && (
                    <Pill kind="primary" onClick={handleStartVerification} disabled={isOnboarding}>
                      {isOnboarding ? 'REDIRECTING…' : verificationStatus === 'requires_action' ? 'CONTINUE VERIFICATION' : verificationStatus === 'rejected' ? 'RETRY VERIFICATION' : 'VERIFY IDENTITY'}
                    </Pill>
                  )}
                </div>
                {!isLoadingVerification && verificationStatus === 'requires_action' && verification?.currentlyDue?.length > 0 && (
                  <div style={{ padding: '12px 14px', borderRadius: 16, background: A.canvas, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <FieldLabel>THEY WANT:</FieldLabel>
                    <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: A.grey600, lineHeight: 1.6 }}>
                      {verification.currentlyDue.map((item) => <li key={item}>{formatRequirement(item)}</li>)}
                    </ul>
                  </div>
                )}
                {!isLoadingVerification && verificationStatus === 'pending' && recheckLine(A.held)}
                {!isLoadingVerification && verificationStatus === 'requires_action' && recheckLine(A.live)}
                {verificationError && <span role="alert" style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', color: A.danger }}>{verificationError}</span>}
              </Card>
            </section>

            {/* MONEY */}
            <section id="set-money" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <SectionHead label="MONEY" />

              {/* Oct 2026: money is handled in the wallet — CASH OUT lives there, for hosts and
                  guests alike, with Stripe's payout fee as the only charge. This card just points. */}
              <Card pad={cardPad} gap={18} data-testid="money-card">
                <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '.12em' }}>YOUR MONEY</span>
                    <span style={{ fontSize: 10, letterSpacing: '.14em', color: A.grey500 }}>SPENDABLE BALANCE</span>
                    <span style={{ fontSize: 36, fontWeight: 700, lineHeight: 1 }}>{money(balance)}</span>
                  </div>
                  <Pill kind="primary" height={44} style={{ padding: '0 22px' }} onClick={() => navigate('/wallet')}>OPEN WALLET →</Pill>
                </div>
                <Prose color={A.grey500}>
                  Add money, send it, or cash out to the bank you connected above — all from your wallet.
                  Cashing out costs only Stripe's payout fee; Portal takes nothing on the way out.
                </Prose>
              </Card>

              <Card pad={cardPad} gap={14}>
                <CardTitle>GUEST CHECKOUT CURRENCY</CardTitle>
                <Prose>Choose the currency displayed to guests purchasing tickets via guest checkout.</Prose>
                <div style={{ display: 'flex', gap: 6 }}>
                  {[['cad', 'CAD (CA$)'], ['usd', 'USD ($)']].map(([code, label]) => (
                    <Pill key={code} kind={currency === code ? 'on' : 'secondary'} onClick={() => pickCurrency(code)} aria-pressed={currency === code} disabled={currencyBusy}>{label}</Pill>
                  ))}
                </div>
              </Card>
            </section>

            {/* PRIVACY */}
            <section id="set-privacy" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <SectionHead label="PRIVACY" />
              <BlockedUsersSection pad={cardPad} />
            </section>

            {/* DELETE ACCOUNT */}
            <section id="set-danger" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <SectionHead label="DELETE ACCOUNT" color={A.danger} />
              <Card pad={cardPad} gap={14} style={{ border: `2px solid ${A.dangerLine}` }} data-testid="delete-account-section">
                <Prose>Permanently delete your account and all associated data. This action cannot be undone.</Prose>
                {!showDeleteConfirm ? (
                  <Pill kind="dangerSoft" style={{ alignSelf: 'flex-start' }} onClick={() => setShowDeleteConfirm(true)} data-testid="delete-account-btn">DELETE MY ACCOUNT</Pill>
                ) : (
                  <>
                    <FieldLabel htmlFor="del-confirm">
                      TYPE DELETE TO CONFIRM.{balance > 0 ? ` YOUR ${money(balance)} BALANCE MUST BE CASHED OUT FIRST.` : ''}
                    </FieldLabel>
                    <Prose color={A.grey500}>This will permanently delete your account, cancel your hosted events, and remove your profile.</Prose>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <Input
                        id="del-confirm"
                        danger
                        value={deleteConfirmText}
                        onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
                        placeholder="DELETE"
                        data-testid="delete-confirm-input"
                        style={{ width: 180, height: 44, padding: '0 16px', fontSize: 13, fontWeight: 700, letterSpacing: '.14em' }}
                      />
                      <Pill kind="danger" onClick={handleDeleteAccount} disabled={deleteConfirmText !== 'DELETE' || isDeleting} data-testid="confirm-delete-btn">
                        {isDeleting ? 'DELETING…' : 'DELETE FOREVER'}
                      </Pill>
                      <Pill kind="secondary" onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); setDeleteError(null); }} data-testid="cancel-delete-btn">CANCEL</Pill>
                    </div>
                    {deleteError && <span role="alert" data-testid="delete-error" style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', color: A.danger }}>{deleteError}</span>}
                  </>
                )}
              </Card>
            </section>
          </div>
        </div>
      </main>
    </PageShell>
  );
}
